import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { createPost } from "../lib/api";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreatePostSchema } from "../lib/api";
import { z } from "zod";

export default function CreatePostPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(CreatePostSchema),
    defaultValues: {
      title: "",
      body: "",
      author: "",
      tags: [],
    },
  });

  const mutation = useMutation({
    mutationFn: (data: z.infer<typeof CreatePostSchema>) => createPost(data),
    onSuccess: (post) => {
      queryClient.invalidateQueries({ queryKey: ["posts"] });
      navigate(`/posts/${post._id}`);
    },
  });

  return (
    <div>
      <div className="mb-8 glass p-8">
        <h1>Create New Post</h1>
        <p className="mt-2">Share your thoughts with the world</p>
      </div>

      <form
        onSubmit={handleSubmit((data) => mutation.mutate(data))}
        className="space-y-6 card"
      >
        <div>
          <label htmlFor="title" className="label">
            Title
          </label>
          <input
            type="text"
            id="title"
            {...register("title")}
            className="input mt-1"
          />
          {errors.title && (
            <p
              className="mt-1 text-sm"
              style={{ color: "var(--color-binary-rose)" }}
            >
              {errors.title.message as string}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="author" className="label">
            Author
          </label>
          <input
            type="text"
            id="author"
            {...register("author")}
            className="input mt-1"
          />
          {errors.author && (
            <p
              className="mt-1 text-sm"
              style={{ color: "var(--color-binary-rose)" }}
            >
              {errors.author.message as string}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="body" className="label">
            Content
          </label>
          <textarea
            id="body"
            rows={10}
            {...register("body")}
            className="input mt-1"
          />
          {errors.body && (
            <p
              className="mt-1 text-sm"
              style={{ color: "var(--color-binary-rose)" }}
            >
              {errors.body.message as string}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="tags" className="label">
            Tags (comma-separated)
          </label>
          <input
            type="text"
            id="tags"
            {...register("tags", {
              setValueAs: (value: string | undefined) =>
                String(value || "")
                  .split(",")
                  .map((tag) => tag.trim())
                  .filter(Boolean),
            })}
            className="input mt-1"
          />
          {errors.tags && (
            <p
              className="mt-1 text-sm"
              style={{ color: "var(--color-binary-rose)" }}
            >
              {errors.tags.message as string}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate("/posts")}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || mutation.isPending}
            className="btn btn-primary"
          >
            Create Post
          </button>
        </div>
      </form>
    </div>
  );
}
